import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Tag, Switch, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';
import ImageUpload from '../components/ImageUpload';

const Categories: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  // 新增/编辑在途锁（Modal 的 onOk 不感知 promise）+ 行内启停在途的行 ID
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const res: any = await request.get('/category');
      setList(res || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      if (editItem) {
        await request.put(`/category/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/category', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  // 行内启停：一次只允许一处在途，其余按钮置灰，避免连点把状态翻回原值
  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/category/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  // 行内切换“是否在首页特色专区展示”（与启停共用行内在途锁）
  const toggleFeature = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/category/${record.id}`, { showFeature: !record.showFeature });
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      content: '分类下有商品时无法删除，需先将商品改设其他分类',
      onOk: async () => {
        await request.delete(`/category/${id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '图标', dataIndex: 'icon', width: 80,
      render: (v: string) => v ? <img src={v} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, background: '#f9fafb' }} /> : '-',
    },
    { title: '分类名称', dataIndex: 'name' },
    { title: '排序', dataIndex: 'sort', width: 80 },
    {
      title: '特色专区', dataIndex: 'showFeature', width: 120,
      render: (v: any, record: any) => (
        <Switch
          checked={!!v}
          checkedChildren="展示"
          unCheckedChildren="隐藏"
          loading={busyId === record.id}
          disabled={busyId !== null && busyId !== record.id}
          onChange={() => toggleFeature(record)}
        />
      ),
    },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? <Tag color="success">启用</Tag> : <Tag>禁用</Tag> },
    {
      title: '操作', width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => { setEditItem(record); form.setFieldsValue(record); setModalOpen(true); }}>编辑</Button>
          <Button
            size="small"
            loading={busyId === record.id}
            disabled={busyId !== null && busyId !== record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>分类管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditItem(null); form.resetFields(); setModalOpen(true); }}>
          新增分类
        </Button>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} pagination={false} />
      <Modal title={editItem ? '编辑分类' : '新增分类'} open={modalOpen}
        onOk={handleSubmit} onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="分类名称" rules={[{ required: true }]}>
            <Input placeholder="如：保健品、医疗器械" />
          </Form.Item>
          <Form.Item name="icon" label="分类图标" tooltip="首页特色专区展示，不传则仅显示名称">
            <ImageUpload tip="建议 120×120 透明底 png / webp，不超过 5MB" />
          </Form.Item>
          <Form.Item name="sort" label="排序" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
